import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { ReactSortable } from 'react-sortablejs'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Dialog from '@radix-ui/react-dialog'
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  GripVertical,
  Heart,
  Link as LinkIcon,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Repeat2,
  Settings,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X
} from 'lucide-react'
import { api } from '../lib/api'
import { Link } from '../lib/router'
import type { BoardColumnColor, GeneratedPost, GenerationColumn, GenerationSource } from '../lib/types'
import { formatCount, formatRelative } from '../lib/format'
import { Button, Field, IconButton, Input, Modal, Notice, Select, Spinner, Textarea } from '../ds'
import { errorText, useToast } from './Toaster'
import { useConfirm } from './Confirm'

type GeneratedCard = GeneratedPost & { id: string }
type GeneratedColumns = Record<string, GeneratedCard[]>

const PAGE_SIZE = 25

const GENERATION_COLUMN_COLORS: { value: BoardColumnColor; label: string }[] = [
  { value: 'gray', label: 'Серый' },
  { value: 'blue', label: 'Синий' },
  { value: 'amber', label: 'Янтарный' },
  { value: 'green', label: 'Зелёный' },
  { value: 'red', label: 'Красный' },
  { value: 'teal', label: 'Бирюзовый' },
  { value: 'purple', label: 'Фиолетовый' },
  { value: 'pink', label: 'Розовый' },
  { value: 'brown', label: 'Коричневый' },
  { value: 'olive', label: 'Оливковый' }
]

function columnsShape(columns: GenerationColumn[]): GeneratedColumns {
  return Object.fromEntries(columns.map((column) => [column.id, []]))
}

function countsShape(columns: GenerationColumn[]): Record<string, number> {
  return Object.fromEntries(columns.map((column) => [column.id, 0]))
}

function sameColumnOrder(a: GenerationColumn[], b: GenerationColumn[]): boolean {
  return a.length === b.length && a.every((column, index) => column.id === b[index]?.id)
}

export function GenerationWorkspace({
  projectId,
  projectName,
  onOpenSettings
}: {
  projectId: string
  projectName: string
  onOpenSettings: () => void
}) {
  const { notify } = useToast()
  const confirm = useConfirm()
  const [sources, setSources] = useState<GenerationSource[]>([])
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set())
  const [columns, setColumns] = useState<GenerationColumn[]>([])
  const [cards, setCards] = useState<GeneratedColumns>({})
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [loadingCols, setLoadingCols] = useState<Set<string>>(new Set())
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const [theme, setTheme] = useState('')
  const [toneNotes, setToneNotes] = useState('')
  const [count, setCount] = useState(10)
  const [generating, setGenerating] = useState(false)
  const [editingColumn, setEditingColumn] = useState<GenerationColumn | null>(null)
  const [deletingColumn, setDeletingColumn] = useState<GenerationColumn | null>(null)
  const [peekCard, setPeekCard] = useState<GeneratedCard | null>(null)

  const sourceById = useMemo(() => new Map(sources.map((source) => [source.id, source])), [sources])

  function loadMeta() {
    setError('')
    Promise.all([api.listGenerationSources(projectId), api.listGenerationColumns(projectId)])
      .then(([sourceRes, columnRes]) => {
        const nextSources = sourceRes.sources.filter((source) => Boolean(source.id))
        const nextColumns = columnRes.columns.filter((column) => Boolean(column.id))
        setSources(nextSources)
        setColumns(nextColumns)
        setSelectedSourceIds((prev) => {
          if (prev.size > 0) {
            const live = new Set(nextSources.map((source) => source.id).filter((id): id is string => Boolean(id)))
            return new Set([...prev].filter((id) => live.has(id)))
          }
          return new Set(nextSources.map((source) => source.id).filter((id): id is string => Boolean(id)))
        })
      })
      .catch((err) => setError(errorText(err)))
  }

  function loadColumn(columnId: string, reset: boolean) {
    setLoadingCols((prev) => new Set(prev).add(columnId))
    const offset = reset ? 0 : (cards[columnId]?.length ?? 0)
    api
      .listGeneratedPosts(projectId, { columnId, limit: PAGE_SIZE, offset })
      .then((res) => {
        const fresh = res.generatedPosts.filter((post): post is GeneratedCard => Boolean(post.id))
        setCards((prev) => ({ ...prev, [columnId]: reset ? fresh : [...(prev[columnId] || []), ...fresh] }))
        setTotals((prev) => ({ ...prev, [columnId]: res.pagination.total }))
      })
      .catch((err) => setError(errorText(err)))
      .finally(() => {
        setLoadingCols((prev) => {
          const next = new Set(prev)
          next.delete(columnId)
          return next
        })
      })
  }

  function reloadBoard(nextColumns = columns) {
    setCards(columnsShape(nextColumns))
    setTotals(countsShape(nextColumns))
    nextColumns.forEach((column) => loadColumn(column.id, true))
    setReady(true)
  }

  useEffect(loadMeta, [projectId])

  const columnIdsKey = columns.map((column) => column.id).sort().join(',')
  useEffect(() => {
    if (columns.length === 0) {
      setReady(true)
      return
    }
    reloadBoard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, columnIdsKey])

  async function generate() {
    if (generating) return
    setGenerating(true)
    setError('')
    try {
      const explicitSourceIds = sources.length ? [...selectedSourceIds] : undefined
      const res = await api.generatePosts(projectId, {
        theme: theme.trim(),
        toneNotes: toneNotes.trim(),
        count,
        sourceIds: explicitSourceIds
      })
      notify(`Готово: ${res.generatedPosts.length} вариантов`)
      reloadBoard()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setGenerating(false)
    }
  }

  async function addSource(input: { type: GenerationSource['type']; title?: string; url?: string; contentText?: string }) {
    const res = await api.createGenerationSource(projectId, input)
    notify('Источник добавлен')
    setSources((prev) => [res.source, ...prev])
    if (res.source.id) {
      setSelectedSourceIds((prev) => new Set(prev).add(res.source.id!))
    }
  }

  async function removeSource(source: GenerationSource) {
    if (!source.id) return
    const ok = await confirm({ title: 'Удалить источник?', message: `«${source.title}» больше не будет использоваться в генерации.` })
    if (!ok) return
    try {
      await api.deleteGenerationSource(source.id)
      setSources((prev) => prev.filter((item) => item.id !== source.id))
      setSelectedSourceIds((prev) => {
        const next = new Set(prev)
        next.delete(source.id!)
        return next
      })
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  function toggleSource(id: string) {
    setSelectedSourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleColumnsChange(nextColumns: GenerationColumn[]) {
    if (sameColumnOrder(columns, nextColumns)) {
      return
    }
    setColumns(nextColumns)
    api
      .reorderGenerationColumns(projectId, nextColumns.map((column) => column.id))
      .catch((err) => {
        notify(errorText(err), 'error')
        loadMeta()
      })
  }

  async function addColumn(name: string) {
    await api.createGenerationColumn(projectId, { name })
    notify('Колонка добавлена')
    loadMeta()
  }

  async function saveColumn(columnId: string, input: { name: string; color: BoardColumnColor }) {
    await api.updateGenerationColumn(columnId, input)
    notify('Колонка обновлена')
    setEditingColumn(null)
    loadMeta()
  }

  async function deleteColumn(columnId: string, moveToColumnId?: string) {
    await api.deleteGenerationColumn(columnId, { moveToColumnId })
    notify('Колонка удалена')
    setDeletingColumn(null)
    loadMeta()
  }

  function handleColumnChange(columnId: string, nextList: GeneratedCard[]) {
    const delta = nextList.length - (cards[columnId]?.length ?? 0)
    if (delta !== 0) {
      setTotals((prev) => ({ ...prev, [columnId]: Math.max(0, (prev[columnId] ?? 0) + delta) }))
    }
    const normalized = nextList.map((item) => (item.columnId !== columnId ? { ...item, columnId } : item))
    setCards((prev) => ({ ...prev, [columnId]: normalized }))
    api
      .reorderGeneratedPosts(projectId, columnId, normalized.map((item) => item.id))
      .catch((err) => {
        notify(errorText(err), 'error')
        reloadBoard()
      })
  }

  // Точечно заменяем одну карточку в стейте доски (без перезагрузки всей доски,
  // чтобы оценка/правка не мигали весь канбан). Если карточка сменила колонку —
  // переносим её и поправляем счётчики.
  function replaceCardInBoard(updated: GeneratedCard, prevColumnId: string | null) {
    const targetColumnId = updated.columnId
    const moved = prevColumnId !== targetColumnId
    setCards((prev) => {
      const next: GeneratedColumns = { ...prev }
      if (moved && prevColumnId && next[prevColumnId]) {
        next[prevColumnId] = next[prevColumnId].filter((item) => item.id !== updated.id)
      }
      if (targetColumnId) {
        const list = next[targetColumnId] ? [...next[targetColumnId]] : []
        const index = list.findIndex((item) => item.id === updated.id)
        if (index >= 0) {
          list[index] = updated
        } else {
          list.unshift(updated)
        }
        next[targetColumnId] = list
      }
      return next
    })
    if (moved) {
      setTotals((prev) => ({
        ...prev,
        ...(prevColumnId ? { [prevColumnId]: Math.max(0, (prev[prevColumnId] ?? 1) - 1) } : {}),
        ...(targetColumnId ? { [targetColumnId]: (prev[targetColumnId] ?? 0) + 1 } : {})
      }))
    }
  }

  // Общий патч карточки: обновляем БД, синхронизируем открытый peek и точечно доску.
  async function patchCard(card: GeneratedCard, patch: Parameters<typeof api.updateGeneratedPost>[1]) {
    try {
      const res = await api.updateGeneratedPost(card.id, patch)
      const updated = res.generatedPost
      if (updated.id) {
        setPeekCard((prev) => (prev && prev.id === card.id ? (updated as GeneratedCard) : prev))
        replaceCardInBoard(updated as GeneratedCard, card.columnId)
      }
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  async function moveCardToColumn(card: GeneratedCard, columnId: string) {
    await patchCard(card, { columnId })
  }

  // Лайк/дизлайк — сигнал для дообучения (liked → в плюс, rejected → «чего избегать»).
  // Колонками управляет пользователь сам (DnD / селект), фидбек на них не завязан.
  // Повторный клик по активной оценке снимает её (neutral).
  async function setFeedback(card: GeneratedCard, feedback: 'liked' | 'rejected') {
    await patchCard(card, { feedback: card.feedback === feedback ? 'neutral' : feedback })
  }

  async function saveCardText(card: GeneratedCard, text: string) {
    await patchCard(card, { text })
  }

  async function saveReason(card: GeneratedCard, rejectedReason: string) {
    await patchCard(card, { rejectedReason })
  }

  async function removeCard(card: GeneratedCard) {
    const ok = await confirm({ title: 'Удалить пост?', message: 'Сгенерированный пост будет удалён безвозвратно.' })
    if (!ok) return
    try {
      await api.deleteGeneratedPost(card.id)
      setPeekCard((prev) => (prev && prev.id === card.id ? null : prev))
      if (card.columnId) {
        const columnId = card.columnId
        setCards((prev) => ({ ...prev, [columnId]: (prev[columnId] || []).filter((item) => item.id !== card.id) }))
        setTotals((prev) => ({ ...prev, [columnId]: Math.max(0, (prev[columnId] ?? 1) - 1) }))
      }
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  async function copyCard(card: GeneratedCard) {
    try {
      await navigator.clipboard.writeText(card.text)
      notify('Скопировано')
    } catch {
      notify('Не удалось скопировать', 'error')
    }
  }

  if (error) {
    return (
      <>
        <GenerationSidebar
          sources={sources}
          selectedSourceIds={selectedSourceIds}
          theme={theme}
          toneNotes={toneNotes}
          count={count}
          generating={generating}
          onTheme={setTheme}
          onToneNotes={setToneNotes}
          onCount={setCount}
          onGenerate={generate}
          onAddSource={addSource}
          onToggleSource={toggleSource}
          onRemoveSource={removeSource}
          projectName={projectName}
          onOpenSettings={onOpenSettings}
        />
        <main className="proj-main generation-main">
          <Notice>{error}</Notice>
        </main>
      </>
    )
  }

  return (
    <>
      <GenerationSidebar
        sources={sources}
        selectedSourceIds={selectedSourceIds}
        theme={theme}
        toneNotes={toneNotes}
        count={count}
        generating={generating}
        onTheme={setTheme}
        onToneNotes={setToneNotes}
        onCount={setCount}
        onGenerate={generate}
        onAddSource={addSource}
        onToggleSource={toggleSource}
        onRemoveSource={removeSource}
        projectName={projectName}
        onOpenSettings={onOpenSettings}
      />

      <main className="proj-main generation-main">
        {!ready ? (
          <div className="generation-loading">
            <Spinner />
          </div>
        ) : (
          <GenerationBoard
            columns={columns}
            cards={cards}
            totals={totals}
            loadingCols={loadingCols}
            sourceById={sourceById}
            onColumnsChange={handleColumnsChange}
            onColumnChange={handleColumnChange}
            onLoadMore={(columnId) => loadColumn(columnId, false)}
            onAddColumn={addColumn}
            onRenameColumn={setEditingColumn}
            onDeleteColumn={setDeletingColumn}
            onOpenCard={setPeekCard}
          />
        )}
      </main>

      {editingColumn && (
        <GenerationColumnSettingsModal
          column={editingColumn}
          onClose={() => setEditingColumn(null)}
          onSubmit={(input) => saveColumn(editingColumn.id, input)}
        />
      )}
      {deletingColumn && (
        <GenerationDeleteColumnModal
          column={deletingColumn}
          columns={columns}
          total={totals[deletingColumn.id] ?? 0}
          onClose={() => setDeletingColumn(null)}
          onDelete={(moveToColumnId) => deleteColumn(deletingColumn.id, moveToColumnId)}
        />
      )}
      {peekCard && (
        <GeneratedPostPeek
          key={peekCard.id}
          card={peekCard}
          columns={columns}
          sources={peekCard.sourceIds
            .map((id) => sourceById.get(id))
            .filter((source): source is GenerationSource => Boolean(source))}
          onClose={() => setPeekCard(null)}
          onCopy={copyCard}
          onFeedback={setFeedback}
          onMove={moveCardToColumn}
          onSaveText={saveCardText}
          onSaveReason={saveReason}
          onDelete={removeCard}
        />
      )}
    </>
  )
}

function GenerationSidebar({
  sources,
  selectedSourceIds,
  theme,
  toneNotes,
  count,
  generating,
  onTheme,
  onToneNotes,
  onCount,
  onGenerate,
  onAddSource,
  onToggleSource,
  onRemoveSource,
  projectName,
  onOpenSettings
}: {
  sources: GenerationSource[]
  selectedSourceIds: Set<string>
  theme: string
  toneNotes: string
  count: number
  generating: boolean
  onTheme: (value: string) => void
  onToneNotes: (value: string) => void
  onCount: (value: number) => void
  onGenerate: () => void
  onAddSource: (input: { type: GenerationSource['type']; title?: string; url?: string; contentText?: string }) => Promise<void>
  onToggleSource: (id: string) => void
  onRemoveSource: (source: GenerationSource) => void
  projectName: string
  onOpenSettings: () => void
}) {
  return (
    <aside className="proj-sidebar generation-sidebar">
      <div className="proj-side-head">
        <Link href="/" className="back-link">
          <ArrowLeft size={14} /> К проектам
        </Link>
        <div className="proj-side-title">
          <h1>{projectName}</h1>
          <IconButton label="Настройки проекта" onClick={onOpenSettings}>
            <Settings size={16} />
          </IconButton>
        </div>
        <div>
          <div className="sidebar-section-label">Генерация</div>
          <h2 className="generation-side-title">Посты из контекста компании</h2>
        </div>
        <Field label="Тема">
          <Textarea
            value={theme}
            onChange={(event) => onTheme(event.target.value)}
            placeholder="Например: новый кейс, частая боль клиентов, запуск функции"
            rows={3}
          />
        </Field>
        <Field label="Тональность">
          <Textarea
            value={toneNotes}
            onChange={(event) => onToneNotes(event.target.value)}
            placeholder="Спокойно, экспертно, без продажного тона"
            rows={3}
          />
        </Field>
        <Field label="Количество">
          <Select
            value={String(count)}
            onValueChange={(value) => onCount(Number(value))}
            options={[10, 12, 15].map((n) => ({ value: String(n), label: `${n} постов` }))}
          />
        </Field>
        <Button variant="primary" block disabled={generating} onClick={onGenerate}>
          {generating ? <Spinner size={14} /> : <Sparkles size={14} />}
          {generating ? 'Генерирую…' : 'Сгенерировать'}
        </Button>
      </div>

      <SourceComposer onAdd={onAddSource} />

      <div className="generation-sources">
        <div className="sidebar-section-label">Источники</div>
        {sources.length === 0 ? (
          <div className="generation-empty">Добавьте сайт, статью, кейс или заметку. ИИ будет учитывать их при генерации.</div>
        ) : (
          sources.map((source) => (
            <div key={source.id} className="generation-source-item">
              <button
                type="button"
                className={['kw-check', source.id && selectedSourceIds.has(source.id) ? 'on' : ''].filter(Boolean).join(' ')}
                onClick={() => source.id && onToggleSource(source.id)}
                aria-label="Использовать источник"
              >
                {source.id && selectedSourceIds.has(source.id) ? <CheckMark /> : null}
              </button>
              <div className="generation-source-main">
                <div className="generation-source-title">
                  {source.type === 'url' ? <LinkIcon size={13} /> : <FileText size={13} />}
                  <span>{source.title}</span>
                </div>
                <div className="generation-source-sub">{source.type === 'url' ? source.url : `${source.contentText.length} символов`}</div>
              </div>
              <IconButton label="Удалить источник" onClick={() => onRemoveSource(source)}>
                <Trash2 size={14} />
              </IconButton>
            </div>
          ))
        )}
      </div>
    </aside>
  )
}

function SourceComposer({
  onAdd
}: {
  onAdd: (input: { type: GenerationSource['type']; title?: string; url?: string; contentText?: string }) => Promise<void>
}) {
  const [type, setType] = useState<GenerationSource['type']>('url')
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [contentText, setContentText] = useState('')
  const [saving, setSaving] = useState(false)
  const { notify } = useToast()

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSaving(true)
    try {
      await onAdd({ type, title: title.trim(), url: url.trim(), contentText: contentText.trim() })
      setTitle('')
      setUrl('')
      setContentText('')
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="source-composer" onSubmit={submit}>
      <div className="segmented">
        <button type="button" className={type === 'url' ? 'active' : ''} onClick={() => setType('url')}>
          Ссылка
        </button>
        <button type="button" className={type === 'text' ? 'active' : ''} onClick={() => setType('text')}>
          Текст
        </button>
      </div>
      <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Название, опционально" />
      {type === 'url' ? (
        <Input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://..." />
      ) : (
        <Textarea value={contentText} onChange={(event) => setContentText(event.target.value)} placeholder="Вставьте кейс, статью или заметку" rows={5} />
      )}
      <Button type="submit" variant="secondary" block disabled={saving || (type === 'url' ? !url.trim() : contentText.trim().length < 20)}>
        {saving ? <Spinner size={14} /> : <Plus size={14} />}
        Добавить источник
      </Button>
    </form>
  )
}

function GenerationBoard({
  columns,
  cards,
  totals,
  loadingCols,
  sourceById,
  onColumnsChange,
  onColumnChange,
  onLoadMore,
  onAddColumn,
  onRenameColumn,
  onDeleteColumn,
  onOpenCard
}: {
  columns: GenerationColumn[]
  cards: GeneratedColumns
  totals: Record<string, number>
  loadingCols: Set<string>
  sourceById: Map<string | null, GenerationSource>
  onColumnsChange: (columns: GenerationColumn[]) => void
  onColumnChange: (columnId: string, nextList: GeneratedCard[]) => void
  onLoadMore: (columnId: string) => void
  onAddColumn: (name: string) => Promise<void>
  onRenameColumn: (column: GenerationColumn) => void
  onDeleteColumn: (column: GenerationColumn) => void
  onOpenCard: (card: GeneratedCard) => void
}) {
  return (
    <div className="board generation-board">
      <ReactSortable<GenerationColumn>
        tag="div"
        className="board-cols"
        list={columns}
        setList={onColumnsChange}
        animation={150}
        handle=".board-col-drag"
        ghostClass="column-ghost"
      >
        {columns.map((column) => {
          const list = cards[column.id] || []
          const total = totals[column.id] ?? 0
          const hasMore = list.length < total
          const loading = loadingCols.has(column.id)
          return (
            <div key={column.id} className={`board-col board-col-color-${column.color || 'gray'}`}>
              <div className="board-col-head">
                <button type="button" className="board-col-drag" aria-label="Перетащить колонку">
                  <GripVertical size={14} />
                </button>
                <span className="dot" />
                <span className="board-col-title">{column.name}</span>
                <span className="board-col-count">{total}</span>
                <GenerationColumnActions
                  column={column}
                  canDelete={columns.length > 1}
                  onRename={onRenameColumn}
                  onDelete={onDeleteColumn}
                />
              </div>
              <ReactSortable<GeneratedCard>
                tag="div"
                className="board-col-list"
                group="generated-posts"
                animation={150}
                ghostClass="card-ghost"
                list={list}
                setList={(next) => onColumnChange(column.id, next)}
              >
                {list.map((card) => (
                  <GeneratedPostCard
                    key={card.id}
                    card={card}
                    sourceCount={card.sourceIds.length}
                    onOpen={onOpenCard}
                  />
                ))}
              </ReactSortable>
              {hasMore && (
                <button type="button" className="board-col-more" disabled={loading} onClick={() => onLoadMore(column.id)}>
                  {loading ? 'Загрузка…' : `Загрузить ещё (${total - list.length})`}
                </button>
              )}
            </div>
          )
        })}
      </ReactSortable>
      <GenerationAddColumnCard onAdd={onAddColumn} />
    </div>
  )
}

// Тонкая карточка канбана: суть на один взгляд (тема + сниппет + флаг фидбека).
// Полный текст, источники и действия — в peek-панели по клику.
function GeneratedPostCard({
  card,
  sourceCount,
  onOpen
}: {
  card: GeneratedCard
  sourceCount: number
  onOpen: (card: GeneratedCard) => void
}) {
  const evidenceCount = sourceCount + card.basedOn.length
  return (
    <div
      className={['post-card', 'generated-card', card.feedback === 'liked' ? 'liked' : card.feedback === 'rejected' ? 'rejected' : '']
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(card)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(card)
        }
      }}
    >
      {card.theme ? <div className="generated-theme">{card.theme}</div> : null}
      <div className="generated-snippet">{card.text}</div>
      <div className="generated-card-foot">
        {card.feedback === 'liked' ? (
          <span className="gen-flag gen-flag-liked">
            <ThumbsUp size={11} /> Нравится
          </span>
        ) : card.feedback === 'rejected' ? (
          <span className="gen-flag gen-flag-rejected">
            <ThumbsDown size={11} /> Не нравится
          </span>
        ) : null}
        <span className="faint">{formatRelative(card.createdAt)}</span>
        {evidenceCount > 0 ? <span className="faint gen-evidence-count">· {evidenceCount} источн.</span> : null}
      </div>
    </div>
  )
}

// Peek-панель сгенерированного поста: полный текст с правкой, источники,
// залетевшие посты-основания и действия (как у поста доски — справа, поверх).
function GeneratedPostPeek({
  card,
  columns,
  sources,
  onClose,
  onCopy,
  onFeedback,
  onMove,
  onSaveText,
  onSaveReason,
  onDelete
}: {
  card: GeneratedCard
  columns: GenerationColumn[]
  sources: GenerationSource[]
  onClose: () => void
  onCopy: (card: GeneratedCard) => void
  onFeedback: (card: GeneratedCard, feedback: 'liked' | 'rejected') => void
  onMove: (card: GeneratedCard, columnId: string) => void
  onSaveText: (card: GeneratedCard, text: string) => Promise<void>
  onSaveReason: (card: GeneratedCard, reason: string) => Promise<void>
  onDelete: (card: GeneratedCard) => void
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(card.text)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [reason, setReason] = useState(card.rejectedReason)
  const [reasonFocused, setReasonFocused] = useState(false)
  const column = columns.find((col) => col.id === card.columnId) || null
  const reasonDirty = reason.trim() !== (card.rejectedReason || '').trim()

  function commitReason() {
    if (reasonDirty) {
      onSaveReason(card, reason.trim())
    }
  }

  async function saveText() {
    setSaving(true)
    try {
      await onSaveText(card, text.trim() || card.text)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  function copy() {
    onCopy(card)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Dialog.Root open modal={false} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Content
          className="peek"
          aria-label="Сгенерированный пост"
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            const target = event.detail.originalEvent.target as HTMLElement | null
            if (target?.closest('.generated-card')) event.preventDefault()
          }}
        >
          <Dialog.Title className="sr-only">Сгенерированный пост</Dialog.Title>
          <div className="peek-bar">
            <IconButton label="Закрыть" onClick={onClose}>
              <X size={16} />
            </IconButton>
          </div>
          <div className="peek-body">
            <div className="genpeek">
              <div className="genpeek-head">
                {card.theme ? <div className="genpeek-theme">{card.theme}</div> : null}
                <div className="genpeek-meta faint">
                  {column ? <span className="genpeek-col" data-color={column.color}>{column.name}</span> : null}
                  <span>{formatRelative(card.createdAt)}</span>
                </div>
              </div>

              {editing ? (
                <div className="genpeek-edit">
                  <Textarea value={text} onChange={(event) => setText(event.target.value)} rows={8} autoFocus />
                  <div className="row" style={{ gap: 8 }}>
                    <Button variant="primary" size="sm" onClick={saveText} disabled={saving}>
                      {saving ? 'Сохранение…' : 'Сохранить'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setText(card.text)
                        setEditing(false)
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="genpeek-text">{card.text}</div>
              )}

              {card.rationale ? <div className="genpeek-rationale">{card.rationale}</div> : null}

              <div className="genpeek-actions">
                <Button variant="secondary" size="sm" onClick={copy}>
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Скопировано' : 'Копировать'}
                </Button>
                {!editing ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                    <Pencil size={14} /> Править
                  </Button>
                ) : null}
                <span className="genpeek-feedback">
                  <button
                    type="button"
                    className={['genpeek-vote', card.feedback === 'liked' ? 'active-like' : ''].filter(Boolean).join(' ')}
                    onClick={() => onFeedback(card, 'liked')}
                    title="Нравится — пойдёт в плюс при дообучении"
                  >
                    <ThumbsUp size={14} /> Нравится
                  </button>
                  <button
                    type="button"
                    className={['genpeek-vote', card.feedback === 'rejected' ? 'active-dislike' : ''].filter(Boolean).join(' ')}
                    onClick={() => onFeedback(card, 'rejected')}
                    title="Не нравится — модель будет такого избегать"
                  >
                    <ThumbsDown size={14} /> Не нравится
                  </button>
                </span>
              </div>

              {card.feedback === 'rejected' ? (
                <div className="genpeek-reason">
                  <span className="genpeek-reason-label faint">Почему не нравится — модель будет этого избегать</span>
                  <div className={['genpeek-reason-field', reasonFocused ? 'is-focused' : ''].filter(Boolean).join(' ')}>
                    <input
                      className="genpeek-reason-input"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      onFocus={() => setReasonFocused(true)}
                      onBlur={() => {
                        setReasonFocused(false)
                        commitReason()
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          commitReason()
                        }
                      }}
                      placeholder="Коротко: что не так"
                      autoFocus
                    />
                    {reasonDirty ? (
                      <button
                        type="button"
                        className="genpeek-reason-save"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={commitReason}
                        disabled={!reason.trim()}
                      >
                        Сохранить
                      </button>
                    ) : reason.trim() ? (
                      <span className="genpeek-reason-saved">
                        <Check size={13} /> Сохранено
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="genpeek-move">
                <span className="faint">Колонка</span>
                <Select
                  value={card.columnId || ''}
                  onValueChange={(value) => onMove(card, value)}
                  aria-label="Переместить в колонку"
                  options={columns.map((col) => ({ value: col.id, label: col.name }))}
                />
              </div>

              {sources.length > 0 ? (
                <div className="genpeek-section">
                  <div className="genpeek-section-title">Контекст компании</div>
                  <div className="genpeek-sources">
                    {sources.map((source) => (
                      <div key={source.id} className="genpeek-source">
                        {source.type === 'url' ? <LinkIcon size={13} /> : <FileText size={13} />}
                        {source.type === 'url' && source.url ? (
                          <a href={source.url} target="_blank" rel="noreferrer">
                            {source.title}
                          </a>
                        ) : (
                          <span>{source.title}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {card.basedOn.length > 0 ? (
                <div className="genpeek-section">
                  <div className="genpeek-section-title">Залетевшие посты ниши · {card.basedOn.length}</div>
                  <div className="genpeek-evidence">
                    {card.basedOn.map((evidence, index) => (
                      <a
                        key={index}
                        className="genpeek-evidence-item"
                        href={evidence.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className="genpeek-evidence-head">
                          <span className="genpeek-evidence-author">@{evidence.authorUsername}</span>
                          <ExternalLink size={12} className="faint" />
                        </div>
                        <div className="genpeek-evidence-text">{evidence.text}</div>
                        <div className="genpeek-evidence-metrics faint">
                          {evidence.metrics.likes != null ? (
                            <span>
                              <Heart size={11} /> {formatCount(evidence.metrics.likes)}
                            </span>
                          ) : null}
                          {evidence.metrics.replies != null ? (
                            <span>
                              <MessageSquare size={11} /> {formatCount(evidence.metrics.replies)}
                            </span>
                          ) : null}
                          {evidence.metrics.reposts != null ? (
                            <span>
                              <Repeat2 size={11} /> {formatCount(evidence.metrics.reposts)}
                            </span>
                          ) : null}
                          {evidence.metrics.views != null ? (
                            <span>
                              <Eye size={11} /> {formatCount(evidence.metrics.views)}
                            </span>
                          ) : null}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : null}

              <button type="button" className="genpeek-delete" onClick={() => onDelete(card)}>
                <Trash2 size={14} /> Удалить пост
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function GenerationColumnActions({
  column,
  canDelete,
  onRename,
  onDelete
}: {
  column: GenerationColumn
  canDelete: boolean
  onRename: (column: GenerationColumn) => void
  onDelete: (column: GenerationColumn) => void
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="board-col-menu" aria-label="Действия с колонкой">
        <MoreHorizontal size={15} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
          <DropdownMenu.Item className="menu-item" onSelect={() => onRename(column)}>
            Настроить
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="menu-sep" />
          <DropdownMenu.Item className="menu-item menu-item-danger" disabled={!canDelete} onSelect={() => onDelete(column)}>
            <Trash2 size={14} /> Удалить
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function GenerationAddColumnCard({ onAdd }: { onAdd: (name: string) => Promise<void> }) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await onAdd(name.trim())
      setName('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="board-add-col" onSubmit={submit}>
      <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Новая колонка" />
      <IconButton label="Добавить колонку" type="submit" tone="ok" disabled={saving || !name.trim()}>
        <Plus size={16} />
      </IconButton>
    </form>
  )
}

function GenerationColumnSettingsModal({
  column,
  onClose,
  onSubmit
}: {
  column: GenerationColumn
  onClose: () => void
  onSubmit: (input: { name: string; color: BoardColumnColor }) => Promise<void>
}) {
  const [name, setName] = useState(column.name)
  const [color, setColor] = useState<BoardColumnColor>(column.color || 'gray')
  const [saving, setSaving] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await onSubmit({ name: name.trim(), color })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Настройки колонки" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Field label="Название">
          <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </Field>
        <Field label="Цвет">
          <div className="color-gallery">
            {GENERATION_COLUMN_COLORS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={['color-swatch', `board-col-color-${item.value}`, color === item.value ? 'selected' : ''].filter(Boolean).join(' ')}
                onClick={() => setColor(item.value)}
                title={item.label}
                aria-label={item.label}
              >
                <span className="dot" />
              </button>
            ))}
          </div>
        </Field>
        <div className="row spread">
          <span />
          <div className="row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Сохраняем…' : 'Сохранить'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

function GenerationDeleteColumnModal({
  column,
  columns,
  total,
  onClose,
  onDelete
}: {
  column: GenerationColumn
  columns: GenerationColumn[]
  total: number
  onClose: () => void
  onDelete: (moveToColumnId?: string) => Promise<void>
}) {
  const targets = columns.filter((item) => item.id !== column.id)
  const [moveToColumnId, setMoveToColumnId] = useState(targets[0]?.id ?? '')
  const [saving, setSaving] = useState(false)
  const hasPosts = total > 0

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (hasPosts && !moveToColumnId) return
    setSaving(true)
    try {
      await onDelete(hasPosts ? moveToColumnId : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Удалить колонку" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Notice>
          Колонка «{column.name}» будет удалена.
          {hasPosts ? ` Посты внутри неё (${total}) нужно перенести в другую колонку.` : ' В ней нет постов.'}
        </Notice>
        {hasPosts && (
          <Field label="Перенести посты в">
            <Select value={moveToColumnId} onValueChange={setMoveToColumnId} options={targets.map((target) => ({ value: target.id, label: target.name }))} />
          </Field>
        )}
        <div className="row spread">
          <span />
          <div className="row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" variant="danger" disabled={saving || (hasPosts && !moveToColumnId)}>
              {saving ? 'Удаляем…' : 'Удалить'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

function CheckMark() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
